import { useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, Star, Trash2, Images } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { fileUrl, openImage } from "@/lib/files";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

/**
 * Machine photo gallery. Photos live in object storage; the primary photo is shown in the
 * machine header, machine cards and on the service report PDF.
 */
export const MachineGallery = ({ machine, onChange, testidPrefix = "machine-gallery" }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [busyId, setBusyId] = useState("");
  const images = machine?.images || [];
  const primaryId = machine?.primary_image_file_id;

  const upload = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (list.length === 0) return;
    setUploading(list.length);
    let latest = null;
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f, f.name || "maskin.jpg");
        const r = await api.post(`/machines/${machine.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        latest = r.data;
        setUploading((n) => n - 1);
      }
      toast.success(list.length === 1 ? "Bilete lagt til." : `${list.length} bilete lagt til.`);
      if (latest) onChange?.(latest);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikkje laste opp biletet.");
      if (latest) onChange?.(latest);
    } finally {
      setUploading(0);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const setPrimary = async (img) => {
    setBusyId(img.id);
    try {
      const r = await api.patch(`/machines/${machine.id}/images/${img.id}/primary`);
      onChange?.(r.data);
      toast.success("Sett som hovudbilete.");
    } catch (e) {
      toast.error("Kunne ikkje endre hovudbilete.");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (img) => {
    setBusyId(img.id);
    try {
      const r = await api.delete(`/machines/${machine.id}/images/${img.id}`);
      onChange?.(r.data);
      toast.success("Bilete fjerna.");
    } catch (e) {
      toast.error("Kunne ikkje fjerne biletet.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <Card className="shadow-sm" data-testid={testidPrefix}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 font-heading text-base font-bold">
          <Images className="h-4 w-4 text-primary" /> Bilete av maskina
          {images.length > 0 && <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">{images.length}</span>}
        </CardTitle>
        <div className="flex gap-1.5">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${testidPrefix}-camera-input`} />
          <input ref={galleryRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${testidPrefix}-upload-input`} />
          <Button size="sm" variant="outline" className="sm:hidden" onClick={() => cameraRef.current?.click()} disabled={uploading > 0} data-testid={`${testidPrefix}-camera-button`}><Camera /> Ta bilete</Button>
          <Button size="sm" variant="outline" onClick={() => galleryRef.current?.click()} disabled={uploading > 0} data-testid={`${testidPrefix}-add-button`}>
            {uploading > 0 ? <Loader2 className="animate-spin" /> : <ImagePlus />} {uploading > 0 ? `Lastar opp ${uploading} …` : "Legg til bilete"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {images.length === 0 ? (
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5"
            data-testid={`${testidPrefix}-empty`}
          >
            <ImagePlus className="h-6 w-6" />
            <span>Ingen bilete enno. Legg til eit bilete av maskina – det blir brukt på maskinkortet og i servicerapporten.</span>
          </button>
        ) : (
          <TooltipProvider delayDuration={200}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid={`${testidPrefix}-grid`}>
              {images.map((img, idx) => {
                const isPrimary = img.id === primaryId;
                return (
                  <div key={img.id} className={cn("group relative overflow-hidden rounded-lg border bg-muted", isPrimary && "ring-2 ring-primary")} data-testid={`${testidPrefix}-item-${idx}`}>
                    <button type="button" onClick={() => openImage(img)} className="block w-full" aria-label={`Opne bilete ${idx + 1}`}>
                      <img src={fileUrl(img.id, { size: "thumb" })} alt={img.caption || `Bilete ${idx + 1}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    </button>
                    {isPrimary && (
                      <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold text-[#0B0D10]" data-testid={`${testidPrefix}-primary-badge`}><Star className="h-3 w-3 fill-current" /> Hovudbilete</span>
                    )}
                    <div className="absolute inset-x-0 bottom-0 flex justify-end gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                      {!isPrimary && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" onClick={() => setPrimary(img)} disabled={busyId === img.id} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-primary hover:text-[#0B0D10]" aria-label="Sett som hovudbilete" data-testid={`${testidPrefix}-set-primary-${idx}`}>
                              {busyId === img.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Sett som hovudbilete</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" onClick={() => remove(img)} disabled={busyId === img.id} className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-destructive" aria-label="Fjern bilete" data-testid={`${testidPrefix}-remove-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>Fjern bilete</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
};
